import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { Toaster } from 'sonner';
import AuthCallback from '@/components/AuthCallback';
import LandingPage from '@/pages/LandingPage';
import AuthPage from '@/pages/AuthPage';
import CompleteProfilePage from '@/pages/CompleteProfilePage';
import AboutPage from '@/pages/AboutPage';
import ContactPage from '@/pages/ContactPage';
import DonationPage from '@/pages/DonationPage';
import DashboardPage from '@/pages/DashboardPage';
import FavoritesPage from '@/pages/FavoritesPage';
import NotificationsPage from '@/pages/NotificationsPage';
import ProfilePage from '@/pages/ProfilePage';
import SuggestCallPage from '@/pages/SuggestCallPage';
import MySuggestionsPage from '@/pages/MySuggestionsPage';
import AdminDashboardPage from '@/pages/AdminDashboardPage';
import AdminContentPage from '@/pages/AdminContentPage';
import AdminMetricsPage from '@/pages/AdminMetricsPage';
import AdminLoginPage from '@/pages/AdminLoginPage';
import AdminEditCallPage from '@/pages/AdminEditCallPage';
import AdminTestimonialsPage from '@/pages/AdminTestimonialsPage';
import CallDetailsPage from '@/pages/CallDetailsPage';
import TermsPage from '@/pages/TermsPage';
import PrivacyPage from '@/pages/PrivacyPage';
import AdminUsersPage from '@/pages/AdminUsersPage';
import AdminBooksPage from '@/pages/AdminBooksPage';
import AdminBlogPage from '@/pages/AdminBlogPage';
import AdminEmailsPage from '@/pages/AdminEmailsPage';
import AdminDonationsPage from '@/pages/AdminDonationsPage';
import AdminReengagementPage from '@/pages/AdminReengagementPage';
import BlogPage from '@/pages/BlogPage';
import BlogPostPage from '@/pages/BlogPostPage';
import '@/App.css';

// Google Analytics Page Tracker
const GoogleAnalyticsTracker = () => {
  const location = useLocation();
  
  useEffect(() => {
    if (typeof window.gtag === 'function') {
      window.gtag('config', 'G-E3CYHFRF93', {
        page_path: location.pathname + location.search
      });
    }
  }, [location]);
  
  return null;
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-4 border-[#002FA7] border-t-transparent rounded-full mx-auto mb-4"></div>
          <p>Carregando...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-4 border-[#002FA7] border-t-transparent rounded-full mx-auto mb-4"></div>
          <p>Carregando...</p>
        </div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <GoogleAnalyticsTracker />
          <div className="App">
            <Routes>
            <Route path="/" element={
              <PublicRoute>
                <LandingPage />
              </PublicRoute>
            } />
            <Route path="/auth" element={
              <PublicRoute>
                <AuthPage />
              </PublicRoute>
            } />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/admin-login" element={<AdminLoginPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/donation" element={<DonationPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/complete-profile" element={
              <ProtectedRoute>
                <CompleteProfilePage />
              </ProtectedRoute>
            } />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            } />
            <Route path="/favorites" element={
              <ProtectedRoute>
                <FavoritesPage />
              </ProtectedRoute>
            } />
            <Route path="/notifications" element={
              <ProtectedRoute>
                <NotificationsPage />
              </ProtectedRoute>
            } />
            <Route path="/profile" element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            } />
            <Route path="/suggest-call" element={
              <ProtectedRoute>
                <SuggestCallPage />
              </ProtectedRoute>
            } />
            <Route path="/my-suggestions" element={
              <ProtectedRoute>
                <MySuggestionsPage />
              </ProtectedRoute>
            } />
            <Route path="/admin" element={
              <ProtectedRoute>
                <AdminDashboardPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/content" element={
              <ProtectedRoute>
                <AdminContentPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/metrics" element={
              <ProtectedRoute>
                <AdminMetricsPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/users" element={
              <ProtectedRoute>
                <AdminUsersPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/call/:id" element={
              <ProtectedRoute>
                <AdminEditCallPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/testimonials" element={
              <ProtectedRoute>
                <AdminTestimonialsPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/books" element={
              <ProtectedRoute>
                <AdminBooksPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/blog" element={
              <ProtectedRoute>
                <AdminBlogPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/emails" element={
              <ProtectedRoute>
                <AdminEmailsPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/donations" element={
              <ProtectedRoute>
                <AdminDonationsPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/reengagement" element={
              <ProtectedRoute>
                <AdminReengagementPage />
              </ProtectedRoute>
            } />
            {/* Blog pages - PUBLIC for SEO */}
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            {/* Call details pages - PUBLIC for SEO - Google can index */}
            {/* Support both /call/:id (legacy) and /chamada/:slug (SEO-friendly) */}
            <Route path="/call/:id" element={<CallDetailsPage />} />
            <Route path="/chamada/:id" element={<CallDetailsPage />} />
          </Routes>
          <Toaster position="top-right" richColors />
        </div>
      </BrowserRouter>
    </AuthProvider>
    </HelmetProvider>
  );
}

export default App;